import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { NetWorthTable } from './NetWorthTable/NetWorthTable';
import { NetWorthChart } from './NetWorthChart/NetWorthChart';
import { NetWorthContent } from './NetWorthStyles';
import { PageDescription } from '../MoneyGuideProStyles';
import { netWorthReducerPrefix } from '../MoneyGuideProConstants';
import { netWorthSelector } from '../MoneyGuidProSelectors';
import { NetWorthTableLoading } from './NetWorthTable/NetWorthTableLoading';
import { NetWorthChartLoading } from './NetWorthChart/NetWorthChartLoading';
import { NetWorthChartEmpty } from './NetWorthChart/NetWorthChartEmpty';
import { NetWorthTableEmpty } from './NetWorthTable/NetWorthTableEmpty';
import { TabProps } from '../MoneyGuideProTypes';
import { Tab } from '../Tab/Tab';

export const NetWorth: React.FC<TabProps> = ({ id, clientId }) => {
  const data = useSelector(netWorthSelector);

  const PageDescriptionComponent = useMemo(
    () => (
      <PageDescription>
        This is your net worth summary as of today. Your net worth is the difference between what
        you own (your assets) and what you owe (your liabilities). To get an accurate net worth
        statement, make certain you have entered all of your assets and liabilities.
      </PageDescription>
    ),
    [],
  );

  const LoadingState = useMemo(
    () => (
      <>
        {PageDescriptionComponent}
        <NetWorthContent>
          <NetWorthChartLoading />
          <NetWorthTableLoading />
        </NetWorthContent>
      </>
    ),
    [PageDescriptionComponent],
  );

  const EmptyState = useMemo(
    () => (
      <>
        {PageDescriptionComponent}
        <NetWorthContent>
          <NetWorthChartEmpty />
          <NetWorthTableEmpty />
        </NetWorthContent>
      </>
    ),
    [PageDescriptionComponent],
  );

  return (
    <Tab
      tabTitle="Net Worth Summary"
      mgpItemId={id}
      clientId={clientId}
      asOfDate={data ? data.asOfDate : null}
      reducerPrefix={netWorthReducerPrefix}
      isEmpty={data === null}
      isPristine={data === undefined}
      LoadingState={LoadingState}
      EmptyState={EmptyState}
    >
      {data ? (
        <>
          {PageDescriptionComponent}
          <NetWorthContent>
            <NetWorthChart data={data.chartData} />
            <NetWorthTable data={data.tableData} />
          </NetWorthContent>
        </>
      ) : null}
    </Tab>
  );
};
